/**
 * 护理方案 API 服务
 * Requirements: 8.1, 8.2, 8.3, 8.4
 */

import { userApiClient } from "@/lib/userApiClient";
import type {
  CareLog,
  CareLogsResponse,
  CreateCareLogRequest,
  CreateCareLogResponse,
  UnlockCareResponse,
  StartCareResponse,
  CompleteCareRequest,
  CompleteCareResponse,
  AssessmentWithTimeline,
} from "@/types/care-plan";

const BASE_PATH = "/assessments";

/**
 * 获取评估详情（包含护理时间轴）
 * @param assessmentId 评估 ID（friendly_id 或数字 ID）
 */
export async function getAssessmentWithTimeline(
  assessmentId: string,
): Promise<AssessmentWithTimeline> {
  return userApiClient.get<AssessmentWithTimeline>(
    `${BASE_PATH}/${assessmentId}`,
  );
}

/**
 * 解锁护理方案
 * @param assessmentId 评估 ID
 * @param paymentToken 支付令牌（预留）
 */
export async function unlockCare(
  assessmentId: string,
  paymentToken?: string,
): Promise<UnlockCareResponse> {
  return userApiClient.post<UnlockCareResponse>(
    `${BASE_PATH}/${assessmentId}/unlock_care`,
    { payment_token: paymentToken },
  );
}

/**
 * 开始护理
 * @param assessmentId 评估 ID
 */
export async function startCare(
  assessmentId: string,
): Promise<StartCareResponse> {
  return userApiClient.post<StartCareResponse>(
    `${BASE_PATH}/${assessmentId}/start_care`,
  );
}

/**
 * 完成护理
 * @param assessmentId 评估 ID
 * @param outcome 护理结果
 */
export async function completeCare(
  assessmentId: string,
  outcome: CompleteCareRequest["outcome"],
): Promise<CompleteCareResponse> {
  return userApiClient.post<CompleteCareResponse>(
    `${BASE_PATH}/${assessmentId}/complete_care`,
    { outcome },
  );
}

/**
 * 获取护理日志列表
 * @param assessmentId 评估 ID
 */
export async function getCareLogs(
  assessmentId: string,
): Promise<CareLogsResponse> {
  return userApiClient.get<CareLogsResponse>(
    `${BASE_PATH}/${assessmentId}/care_logs`,
  );
}

/**
 * 创建护理日志
 * @param assessmentId 评估 ID
 * @param data 日志数据
 */
export async function createCareLog(
  assessmentId: string,
  data: CreateCareLogRequest["care_log"],
): Promise<CreateCareLogResponse> {
  return userApiClient.post<CreateCareLogResponse>(
    `${BASE_PATH}/${assessmentId}/care_logs`,
    { care_log: data },
  );
}

/**
 * 更新护理日志
 * @param assessmentId 评估 ID
 * @param logId 日志 ID
 * @param data 更新数据
 */
export async function updateCareLog(
  assessmentId: string,
  logId: number,
  data: Partial<CreateCareLogRequest["care_log"]>,
): Promise<{ care_log: CareLog; meta: CareLogsMeta }> {
  return userApiClient.patch<{
    care_log: CareLog;
    meta: CareLogsMeta;
  }>(`${BASE_PATH}/${assessmentId}/care_logs/${logId}`, { care_log: data });
}

/**
 * 删除护理日志
 * @param assessmentId 评估 ID
 * @param logId 日志 ID
 */
export async function deleteCareLog(
  assessmentId: string,
  logId: number,
): Promise<void> {
  await userApiClient.delete(`${BASE_PATH}/${assessmentId}/care_logs/${logId}`);
}

/**
 * 记录任务完成
 * @param assessmentId 评估 ID
 * @param taskId 任务 ID
 * @param dayNumber 天数（确保 >= 1）
 */
export async function recordTaskCompleted(
  assessmentId: string,
  taskId: string,
  dayNumber: number,
): Promise<CreateCareLogResponse> {
  // 确保 day_number >= 1，后端验证要求大于 0
  const validDayNumber = Math.max(1, dayNumber);
  return createCareLog(assessmentId, {
    log_type: "task_completed",
    day_number: validDayNumber,
    task_data: { task_id: taskId },
  });
}

/**
 * 记录每日观察
 * @param assessmentId 评估 ID
 * @param status 观察状态
 * @param dayNumber 天数
 * @param notes 备注
 */
export async function recordObservation(
  assessmentId: string,
  status: "improved" | "unchanged" | "worsened",
  dayNumber: number,
  notes?: string,
): Promise<CreateCareLogResponse> {
  return createCareLog(assessmentId, {
    log_type: "observation",
    observation_status: status,
    day_number: dayNumber,
    notes,
  });
}

export const carePlanService = {
  getAssessmentWithTimeline,
  unlockCare,
  startCare,
  completeCare,
  getCareLogs,
  createCareLog,
  updateCareLog,
  deleteCareLog,
  recordTaskCompleted,
  recordObservation,
};

export default carePlanService;
